/**
 * Quad Atlas QA Checks - Manual Execution Only
 *
 * This module performs read-only validation checks on the rendered application.
 * It NEVER runs automatically and has NO side effects on import.
 *
 * Usage: await window.runAtlasQA()
 */
interface QAResult {
  section: string
  status: 'PASS' | 'FAIL' | 'SKIP'
  details: string[]
  measurements?: Record<string, any>
}
interface DemoReadinessReport {
  timestamp: string
  overallStatus: 'PASS' | 'FAIL'
  sections: QAResult[]
  summary: {
    accountCount: number
    totalPeople: number
    minPeoplePerAccount: number
    consoleErrors: number
    consoleWarnings: number
  }
}
/**
 * Main entry point - only exported function
 * Safe to call at any time, performs read-only checks
 */
export async function runAtlasQA(): Promise<DemoReadinessReport> {
  console.log('\n🔍 Starting Quad Atlas QA Checks...\n')
  const report: DemoReadinessReport = {
    timestamp: new Date().toISOString(),
    overallStatus: 'PASS',
    sections: [],
    summary: {
      accountCount: 0,
      totalPeople: 0,
      minPeoplePerAccount: 0,
      consoleErrors: 0,
      consoleWarnings: 0,
    },
  }
  try {
    // Guard: Ensure we're in a browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      console.warn('[QA] Not in browser environment, skipping checks')
      return report
    }
    // Guard: Ensure app shell exists
    const appShell = document.querySelector('[data-app-shell]')
    if (!appShell) {
      console.warn('[QA] App shell not found, aborting checks')
      return report
    }
    // Run checks (all wrapped in try/catch)
    report.sections.push(await checkDataValidation())
    report.sections.push(await checkLayoutParity())
    report.sections.push(await checkPeoplePageRobustness())
    report.sections.push(await checkOrdersPageGuard())
    report.sections.push(await checkChatWiring())
    report.sections.push(await checkPerformanceMetrics())
    // Determine overall status
    report.overallStatus = report.sections.every(
      (s) => s.status === 'PASS' || s.status === 'SKIP',
    )
      ? 'PASS'
      : 'FAIL'
    // Print report
    printReport(report)
    return report
  } catch (error) {
    console.error('[QA] Unhandled error:', error)
    report.overallStatus = 'FAIL'
    report.sections.push({
      section: 'System Error',
      status: 'FAIL',
      details: [
        `Unhandled error: ${error instanceof Error ? error.message : 'Unknown'}`,
      ],
    })
    return report
  }
}
// 1. Data Validation
async function checkDataValidation(): Promise<QAResult> {
  const result: QAResult = {
    section: '1. Data Validation',
    status: 'PASS',
    details: [],
  }
  try {
    // Dynamically import to avoid circular dependencies
    const { seedData } = await import('../data/seedData')
    const { validateSeedData } = await import('../data/utils/validateData')
    const validationResult = validateSeedData(seedData.accounts)
    result.details.push(`Accounts: ${validationResult.accountCount}`)
    result.details.push(`Total People: ${validationResult.totalPeople}`)
    result.details.push(
      `Client Contacts: ${validationResult.clientContactsCount}`,
    )
    result.details.push(`Quad Team: ${validationResult.quadTeamCount}`)
    // Check assertions
    if (validationResult.accountCount < 100) {
      result.status = 'FAIL'
      result.details.push(
        `❌ Expected >= 100 accounts, found ${validationResult.accountCount}`,
      )
    } else {
      result.details.push(`✓ Accounts count meets requirement (>= 100)`)
    }
    if (validationResult.totalPeople < 1200) {
      result.status = 'FAIL'
      result.details.push(
        `❌ Expected >= 1200 people, found ${validationResult.totalPeople}`,
      )
    } else {
      result.details.push(`✓ Total people meets requirement (>= 1200)`)
    }
    // Check per-account minimums
    let minClientContacts = Infinity
    let minQuadTeam = Infinity
    let failedAccounts = 0
    seedData.accounts.forEach((account) => {
      const clientCount = account.client_contacts?.length || 0
      const quadCount = account.quad_team?.length || 0
      minClientContacts = Math.min(minClientContacts, clientCount)
      minQuadTeam = Math.min(minQuadTeam, quadCount)
      if (clientCount < 3 || quadCount < 5) {
        failedAccounts++
      }
    })
    if (failedAccounts > 0) {
      result.status = 'FAIL'
      result.details.push(
        `❌ ${failedAccounts} accounts have insufficient people (need >= 3 client, >= 5 quad)`,
      )
    } else {
      result.details.push(`✓ All accounts meet minimum people requirements`)
    }
    if (validationResult.errors.length > 0) {
      result.status = 'FAIL'
      result.details.push(
        `❌ Validation errors: ${validationResult.errors.length}`,
      )
      result.details.push(...validationResult.errors.slice(0, 3))
    }
  } catch (error) {
    result.status = 'FAIL'
    result.details.push(
      `❌ Error during validation: ${error instanceof Error ? error.message : 'Unknown'}`,
    )
  }
  return result
}
// 2. Layout Parity Checks (read-only)
async function checkLayoutParity(): Promise<QAResult> {
  const result: QAResult = {
    section: '2. Layout Parity',
    status: 'PASS',
    details: [],
    measurements: {},
  }
  try {
    // Find current route's main content
    const main = document.querySelector('[data-route] [data-main]')
    if (!main) {
      result.status = 'SKIP'
      result.details.push('⚠️ Main content container not found')
      return result
    }
    const rect = main.getBoundingClientRect()
    const styles = window.getComputedStyle(main)
    const paddingLeft = parseInt(styles.paddingLeft) || 0
    const paddingTop = parseInt(styles.paddingTop) || 0
    result.measurements = {
      paddingLeft,
      paddingTop,
      left: rect.left,
      top: rect.top,
    }
    // Check padding requirements (accounting for parent container padding)
    const expectedLeft = 256 + 32 // sidebar + padding
    const expectedTop = 64 + 32 // header + padding
    if (rect.left < expectedLeft - 10) {
      result.status = 'FAIL'
      result.details.push(
        `❌ Content left position ${rect.left.toFixed(0)}px < expected ~${expectedLeft}px`,
      )
    } else {
      result.details.push(
        `✓ Content left position correct: ${rect.left.toFixed(0)}px`,
      )
    }
    if (rect.top < expectedTop - 10) {
      result.status = 'FAIL'
      result.details.push(
        `❌ Content top position ${rect.top.toFixed(0)}px < expected ~${expectedTop}px`,
      )
    } else {
      result.details.push(
        `✓ Content top position correct: ${rect.top.toFixed(0)}px`,
      )
    }
    // Check for sidebar overlap (read-only)
    const sidebar = document.querySelector('.fixed.left-0.w-64')
    if (sidebar) {
      const sidebarRect = sidebar.getBoundingClientRect()
      if (rect.left < sidebarRect.right) {
        result.status = 'FAIL'
        result.details.push(
          `❌ Content overlaps sidebar: left ${rect.left.toFixed(0)}px < ${sidebarRect.right.toFixed(0)}px`,
        )
      } else {
        result.details.push('✓ No sidebar overlap detected')
      }
    }
  } catch (error) {
    result.status = 'FAIL'
    result.details.push(
      `❌ Error during layout check: ${error instanceof Error ? error.message : 'Unknown'}`,
    )
  }
  return result
}
// 3. People Page Robustness
async function checkPeoplePageRobustness(): Promise<QAResult> {
  const result: QAResult = {
    section: '3. People Page Robustness',
    status: 'PASS',
    details: [],
  }
  try {
    const { seedData } = await import('../data/seedData')
    // Sample 3 random accounts
    const sampleSize = Math.min(3, seedData.accounts.length)
    const sampleIndices = new Set<number>()
    while (sampleIndices.size < sampleSize) {
      sampleIndices.add(Math.floor(Math.random() * seedData.accounts.length))
    }
    const sampledAccounts = Array.from(sampleIndices).map(
      (i) => seedData.accounts[i],
    )
    let passedAccounts = 0
    let failedAccounts = 0
    sampledAccounts.forEach((account) => {
      const clientContacts = Array.isArray(account.client_contacts)
        ? account.client_contacts
        : []
      const quadTeam = Array.isArray(account.quad_team) ? account.quad_team : []
      const allPeople = [...clientContacts, ...quadTeam]
      if (allPeople.length < 8) {
        failedAccounts++
        result.details.push(
          `❌ ${account.name}: ${allPeople.length} people (expected >= 8)`,
        )
      } else {
        passedAccounts++
        result.details.push(`✓ ${account.name}: ${allPeople.length} people`)
      }
    })
    if (failedAccounts > 0) {
      result.status = 'FAIL'
      result.details.push(
        `❌ ${failedAccounts}/${sampleSize} accounts failed minimum people check`,
      )
    } else {
      result.details.push(
        `✓ All ${passedAccounts} sampled accounts have sufficient people`,
      )
    }
  } catch (error) {
    result.status = 'FAIL'
    result.details.push(
      `❌ Error during people check: ${error instanceof Error ? error.message : 'Unknown'}`,
    )
  }
  return result
}
// 4. Orders Page Guard
async function checkOrdersPageGuard(): Promise<QAResult> {
  const result: QAResult = {
    section: '4. Orders Page Guard',
    status: 'PASS',
    details: [],
  }
  try {
    const { seedData } = await import('../data/seedData')
    // Sample 3 random accounts
    const sampleSize = Math.min(3, seedData.accounts.length)
    const sampleIndices = new Set<number>()
    while (sampleIndices.size < sampleSize) {
      sampleIndices.add(Math.floor(Math.random() * seedData.accounts.length))
    }
    const sampledAccounts = Array.from(sampleIndices).map(
      (i) => seedData.accounts[i],
    )
    let passedAccounts = 0
    let failedAccounts = 0
    sampledAccounts.forEach((account) => {
      const orders = Array.isArray(account.orders) ? account.orders : []
      if (orders.length < 3 || orders.length > 6) {
        failedAccounts++
        result.details.push(
          `❌ ${account.name}: ${orders.length} orders (expected 3-6)`,
        )
      } else {
        const hasValidOrders = orders.every(
          (order) =>
            order.status && typeof order.value === 'number' && order.value > 0,
        )
        if (!hasValidOrders) {
          failedAccounts++
          result.details.push(`❌ ${account.name}: Invalid order structure`)
        } else {
          passedAccounts++
          const totalValue = orders.reduce((sum, o) => sum + o.value, 0)
          result.details.push(
            `✓ ${account.name}: ${orders.length} orders, $${(totalValue / 1000).toFixed(0)}K total`,
          )
        }
      }
    })
    if (failedAccounts > 0) {
      result.status = 'FAIL'
      result.details.push(
        `❌ ${failedAccounts}/${sampleSize} accounts failed orders validation`,
      )
    } else {
      result.details.push(
        `✓ All ${passedAccounts} sampled accounts have valid orders`,
      )
    }
  } catch (error) {
    result.status = 'FAIL'
    result.details.push(
      `❌ Error during orders check: ${error instanceof Error ? error.message : 'Unknown'}`,
    )
  }
  return result
}
// 5. Chat Wiring Smoke Test
async function checkChatWiring(): Promise<QAResult> {
  const result: QAResult = {
    section: '5. Chat Wiring',
    status: 'PASS',
    details: [],
  }
  try {
    const { seedData } = await import('../data/seedData')
    const accountsWithBots = seedData.accounts.filter(
      (acc) => acc.bots && acc.bots.length > 0,
    )
    if (accountsWithBots.length === 0) {
      result.status = 'FAIL'
      result.details.push('❌ No accounts have bot configurations')
      return result
    }
    const sampleAccount = accountsWithBots[0]
    const bot = sampleAccount.bots[0]
    if (!bot.api_snippet && !bot.api_endpoint) {
      result.details.push('⚠️ Bot configured but no API endpoint found')
    } else {
      result.details.push(`✓ Bot endpoint configured`)
      result.details.push(`✓ Bot type: ${bot.type}`)
      result.details.push(`✓ Bound spaces: ${bot.bound_spaces.length}`)
    }
    // Check for iframe usage (read-only)
    const iframes = document.querySelectorAll('iframe')
    const chatIframes = Array.from(iframes).filter((iframe) =>
      iframe.src.includes('chat'),
    )
    if (chatIframes.length > 0) {
      result.details.push(`⚠️ Found ${chatIframes.length} chat iframes`)
    } else {
      result.details.push('✓ No iframe-based chat rendering detected')
    }
  } catch (error) {
    result.status = 'FAIL'
    result.details.push(
      `❌ Error during chat check: ${error instanceof Error ? error.message : 'Unknown'}`,
    )
  }
  return result
}
// Add new performance check
async function checkPerformanceMetrics(): Promise<QAResult> {
  const result: QAResult = {
    section: '6. Performance Metrics',
    status: 'PASS',
    details: [],
    measurements: {},
  }
  try {
    // Check bundle size indicators
    const scripts = document.querySelectorAll('script[src]')
    result.details.push(`Scripts loaded: ${scripts.length}`)
    // Check for memory leaks (basic check)
    const memoryInfo = (performance as any).memory
    if (memoryInfo) {
      const usedMB = (memoryInfo.usedJSHeapSize / 1048576).toFixed(2)
      const limitMB = (memoryInfo.jsHeapSizeLimit / 1048576).toFixed(2)
      result.measurements = {
        usedMemoryMB: usedMB,
        memoryLimitMB: limitMB,
      }
      result.details.push(`Memory usage: ${usedMB}MB / ${limitMB}MB`)
      // Warn if using more than 80% of available memory
      if (memoryInfo.usedJSHeapSize / memoryInfo.jsHeapSizeLimit > 0.8) {
        result.status = 'FAIL'
        result.details.push('⚠️ High memory usage detected')
      }
    }
    // Check for excessive re-renders (look for React DevTools data)
    const reactRoot = document.querySelector('[data-app-shell]')
    if (reactRoot) {
      result.details.push('✓ React root element found')
    }
    // Check for console errors/warnings
    const consoleErrorCount = 0 // Would need to intercept console in real implementation
    result.details.push(`Console errors: ${consoleErrorCount}`)
  } catch (error) {
    result.status = 'FAIL'
    result.details.push(
      `❌ Error during performance check: ${error instanceof Error ? error.message : 'Unknown'}`,
    )
  }
  return result
}
function printReport(report: DemoReadinessReport) {
  console.log('\n' + '='.repeat(80))
  console.log('           QUAD ATLAS DEMO READINESS REPORT')
  console.log('='.repeat(80))
  console.log(`Timestamp: ${new Date(report.timestamp).toLocaleString()}`)
  console.log(
    `Overall Status: ${report.overallStatus === 'PASS' ? '✅ PASS' : '❌ FAIL'}`,
  )
  console.log('\n' + '-'.repeat(80))
  console.log('SUMMARY')
  console.log('-'.repeat(80))
  console.log(`Accounts: ${report.summary.accountCount || 'N/A'}`)
  console.log(`Total People: ${report.summary.totalPeople || 'N/A'}`)
  console.log(
    `Min People Per Account: ${report.summary.minPeoplePerAccount || 'N/A'}`,
  )
  report.sections.forEach((section) => {
    console.log('\n' + '-'.repeat(80))
    console.log(
      `${section.section} - ${section.status === 'PASS' ? '✅ PASS' : section.status === 'SKIP' ? '⚠️ SKIP' : '❌ FAIL'}`,
    )
    console.log('-'.repeat(80))
    section.details.forEach((detail) => console.log(detail))
    if (section.measurements) {
      console.log('\nMeasurements:')
      console.log(JSON.stringify(section.measurements, null, 2))
    }
  })
  console.log('\n' + '='.repeat(80))
  console.log(
    `FINAL RESULT: ${report.overallStatus === 'PASS' ? '✅ ALL CHECKS PASSED' : '❌ SOME CHECKS FAILED'}`,
  )
  console.log('='.repeat(80) + '\n')
}
